import React, { useRef, lazy, Suspense } from "react";

import { BrowserRouter as Router, useLocation } from "react-router-dom";

import { AppProvider } from "./AppContext";
import { useAppContext } from "./AppContext";

import InitialLoading from "./Components/FallBackScreens/InitialLoading";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Sidebar from "./Layouts/Sidebar/Sidebar";

import Login from "./pages/Login";

import LicenseKey from "./pages/LicenseKey";

import useAuth from "./hooks/useAuth";
import useAppData from "./hooks/useAppData";
import useWebSocket from "./hooks/useWebSocket";
import useAlarmWebSocket from "./hooks/useAlarmWebSocket";
import useSimulationData from "./hooks/useSimulationData";
import useBrightness from "./hooks/useInitBrightness";
import useTransmitterRedundancySwitch from "./hooks/useTransmitterRedundancySwitch";
import OperationPdfController from "./Components/OperationPdfController/OperationPdfController";

import useUserActive from "./hooks/useUserActive";

/*** * * ***/

import "./App.css";

/*** * * ***/

const AppRoutes = lazy(() => import("./config/routes"));

/*** * * ***/

function AppContent() {
  const { redundancyFlag, setRedundancyFlag } = useAppContext();

  const location = useLocation();

  const parameters = new URLSearchParams(location.search);

  /* AUTHENTICATION HANDLING */
  const { isLoggedIn, setIsLoggedIn, loading: isLoading } = useAuth();

  /* DATA HANDLING */
  const { processLiveData } = useAppData(isLoggedIn);
  const liveFlag = useRef(true);

  /* PLAYBACK HANDLING */
  const {
    setSimulationPlay,
    simulationData,
    simulationAlarmData,
    setSimulationData,
    setSimulationAlarmData,
    setSliderValue,
    isSliding,
    dataFlag,
    loadingFlag,
    setLoadingFlag,
    isModalOpen,
    setIsModalOpen,
  } = useSimulationData(processLiveData, liveFlag);

  /*** * * ***/

  if (parameters.has("rd")) {
    setRedundancyFlag(parameters.get("rd") === "true");
  }

  /* WEBSOCKET INITIALIZE */
  useWebSocket(
    isLoggedIn,
    liveFlag,
    processLiveData,
    setIsLoggedIn,
    isSliding,
    isModalOpen
  );

  /* ALARM HANDLING */
  useAlarmWebSocket({ isSliding, liveFlag, isLoggedIn });

  /* ADJUST APP BRIGHNESS */
  useBrightness();

  /* TRANSMITTER REDUNDANCY SWITCH */
  useTransmitterRedundancySwitch(redundancyFlag); // works only if transmitter and enabled from .env

  /* User Active */
  useUserActive({isLoggedIn});

  /* CONSOLE */
  if (process.env.NODE_ENV === "production") {
    console.log = () => {};
    console.error = () => {};
    console.debug = () => {};
  }

  /*** * * ***/

  return (
    <>
      <ToastContainer />
      {
        location.pathname === "/license_key" ? (
          <LicenseKey />
        ) : (
          !isLoading && (
            isLoggedIn ? (
              <div className={`App brightness ${(window.location.pathname === "/simulator"? "simulator-active" : "")}`}>
                <OperationPdfController />
                <Suspense fallback={<InitialLoading />}>
                  <AppRoutes
                    isSliding={isSliding}
                    liveFlag={liveFlag}
                    loadingFlag={loadingFlag}
                    setLoadingFlag={setLoadingFlag}
                    dataFlag={dataFlag}
                    simulationObject={{
                      setSimulationData,
                      setSimulationAlarmData,
                      setSimulationPlay,
                      setSliderValue,
                      simulationData,
                      simulationAlarmData,
                      setLoadingFlag,
                      dataFlag,
                      loadingFlag,
                      liveFlag,
                      isModalOpen,
                      setIsModalOpen,
                    }}
                  />
                </Suspense>
                <Sidebar setIsLoggedIn={setIsLoggedIn} />
              </div>
            ) : (
              <Login setIsLoggedIn={setIsLoggedIn} />
            )
          )
        )
      }
    </>
  );
}

/*** * * ***/

function App() {
  return (
    <AppProvider>
      <Router>
        <AppContent/>
      </Router>
    </AppProvider>
  );
}

export default App;
